/**
 * Big press-and-hold microphone button.
 *
 * Behaviour:
 *   - Press (touchstart/mousedown) → start recording, pulse animation.
 *   - Release (touchend/mouseup) → stop and hand the blob to the parent.
 *   - Auto-stop after 5 s handled inside the Recorder.
 *   - Pointer cancellation (e.g. scrolling away) is treated as release.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { Recorder, type RecordResult } from '../lib/recorder';

interface Props {
  onResult: (r: RecordResult) => void;
  onError?: (err: unknown) => void;
  disabled?: boolean;
  label?: string;
}

export function MicButton({ onResult, onError, disabled, label }: Props) {
  const recorder = useRef<Recorder | null>(null);
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);

  useEffect(() => {
    recorder.current = new Recorder(5000);
    return () => recorder.current?.release();
  }, []);

  // Naive VU-meter: bob opacity while recording to show "we hear you".
  useEffect(() => {
    if (!recording) return;
    let raf = 0;
    const loop = () => {
      setLevel(0.6 + 0.4 * Math.abs(Math.sin(performance.now() / 180)));
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [recording]);

  async function start() {
    if (disabled || recording) return;
    try {
      await recorder.current!.start();
      setRecording(true);
    } catch (err) {
      onError?.(err);
    }
  }

  async function stop() {
    if (!recording) return;
    try {
      const r = await recorder.current!.stop();
      setRecording(false);
      onResult(r);
    } catch (err) {
      setRecording(false);
      onError?.(err);
    }
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={recording ? stop : undefined}
      class={`relative w-40 h-40 rounded-full text-white shadow-xl transition-colors
              ${recording ? 'bg-berry' : 'bg-coral'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
      style={recording ? { boxShadow: `0 0 ${24 * level}px rgba(185,74,140,${0.5 * level})` } : undefined}
      aria-label={label ?? 'Press and hold to speak'}
    >
      <span class="text-6xl" aria-hidden="true">
        {recording ? '🔴' : '🎤'}
      </span>
      {recording && (
        <span class="absolute inset-0 rounded-full border-4 border-berry/60 animate-ping" />
      )}
    </button>
  );
}
